/**
 * Corner-box drags shape a smart component, instead of merely stretching it.
 *
 * The viewport box scales a node — it multiplies `transform.scaling` — and
 * MorphStretch reacts to `width`/`height`/`depth`. Two surfaces driving two
 * different properties, which is why the numeric Size panel shaped the geometry
 * while dragging the box only stretched the whole node.
 *
 * The fix is not to blend anything here. It is to re-express the drag as the
 * SIZE it asks for and hand that to MorphStretch, because the engine already
 * does the interesting half: morphs absorb as much of a requested size as their
 * authored range covers, and only the remainder becomes a per-axis scale on the
 * MESHES (see `MorphStretch`). Inside the range the factor is exactly 1 — a
 * pure blendshape edit; past it the morphs pin at their reach and the excess
 * stretches. Seamless falls out of writing the honest size.
 *
 * WHY THIS LIVES IN THE ENGINE. Every part of it is a function of what the
 * component itself reports through `getStretchCapability()` — where its morphs
 * reach, and therefore whether a given size is still shaping or has started
 * stretching. A host cannot answer that without reimplementing the rig's own
 * arithmetic, and two hosts would answer it two different ways. So the engine
 * owns the whole decision and a host supplies only what it alone knows: the
 * live size its gizmo is asking for, and a box to paint.
 *
 * The colour is the second half of the feature and the same argument applies:
 * PURPLE while the morphs are absorbing the drag, the ordinary selection BLUE
 * the moment an axis runs out of range and starts stretching. It answers "am I
 * shaping or stretching?" without reading a number, and it flips mid-drag at
 * the exact size the behaviour changes.
 *
 * A node a Layout sizes is the exception to "write MorphStretch". The solve
 * hands MorphStretch the node's LayoutItem box, and that box wins over
 * MorphStretch's own width/height/depth (`MorphStretch.setResolvedSize`), so a
 * MorphStretch-only write changes nothing on screen. For such a node the drag
 * is written to the LayoutItem as a Fixed size, and MorphStretch receives it
 * through the ordinary layout path. MorphStretch is written too, so its
 * authored size on the axes the drag moved matches its box, as the
 * converter authors them. Axes the drag has not moved are written to neither.
 * The drag measures from the box the solve resolved, which on a Fill axis is
 * the only place the size exists.
 *
 * Undo: live writes during a drag are deliberately un-recorded — one gesture is
 * one entry, built at release from the size the drag started with.
 */

import { Color3 } from "@babylonjs/core/Maths/math.color";

import type { SceneObject } from "../core/SceneObject";
import type { MorphStretchAxisCapability } from "../behaviors/morphstretch";
import type { MorphStretchData } from "../scenedata/behaviors/morphstretchdata";
import type { LayoutItemData } from "../scenedata/behaviors/layoutitemdata";
import { MorphStretch } from "../behaviors/morphstretch";
import { LayoutItem } from "../behaviors/layoutitem";
import { BehaviorCommitCommand } from "./undo/BehaviorCommitCommand";
import { CompositeCommand } from "./undo/CompositeCommand";
import type { UndoCommand } from "./undo/UndoCommand";
import { dispatchBehaviorChanged } from "./undo/editor-events";
import { Logger } from "../utils/Logger";

/** Per-axis reach, as `MorphStretch.getStretchCapability()` reports it. */
export type StretchCapability = Partial<Record<"X" | "Y" | "Z", MorphStretchAxisCapability>>;

/** Which of the two things a requested size on an axis actually does. */
export type SmartScaleRegime = "morph" | "scale";

interface Vec3Like { x: number; y: number; z: number }

export const SMART_AXES = [
    { axis: "X", key: "x", field: "width", mode: "widthMode" },
    { axis: "Y", key: "y", field: "height", mode: "heightMode" },
    { axis: "Z", key: "z", field: "depth", mode: "depthMode" },
] as const;

/** Below this two sizes are the same size, whatever float noise a proxy division adds. */
const SIZE_EPSILON = 1e-6;

/** Selection blue — a plain asset's box, and a smart one past its morph range. */
export const BOX_COLOR_PLAIN = "#2695D6";
/** Smart-scaling purple — the morphs are absorbing this drag. */
export const BOX_COLOR_SMART = "#8B5CF6";

export const colorForRegime = (regime: SmartScaleRegime): string =>
    regime === "morph" ? BOX_COLOR_SMART : BOX_COLOR_PLAIN;

/**
 * The size a drag is asking for on each axis.
 *
 * `liveLocalSize` is the size the gizmo is currently showing, in the
 * component's OWN frame — the only frame MorphStretch speaks, and the one that
 * stays correct when the component is rotated. A host that scales a proxy
 * fitted to world bounds converts by dividing out the object's own scaling
 * before calling; a host that scales the object node directly reads
 * `rest × scaling`.
 *
 * Axes the component cannot size are skipped rather than written at their
 * current value: writing an unchanged field would still count as a change and
 * put an empty entry on the undo stack.
 */
export function requestedSize(
    capability: StretchCapability,
    liveLocalSize: Vec3Like,
): Partial<Record<"width" | "height" | "depth", number>> {
    const out: Record<string, number> = {};
    for (const { axis, key, field } of SMART_AXES) {
        if (!capability[axis]) continue;
        const size = liveLocalSize[key];
        if (!Number.isFinite(size) || size <= 0) continue;
        out[field] = size;
    }
    return out;
}

/**
 * Whether this size is still inside what the morphs can realize.
 *
 * An axis with no morphs is always `scale` — there is nothing to shape, and
 * saying otherwise would paint the box as if a blendshape were moving. Bounds
 * are inclusive: sitting exactly ON the maximum is the last size the morphs
 * realize, not the first one they do not.
 */
export function axisRegime(
    reach: MorphStretchAxisCapability | undefined,
    size: number | undefined,
): SmartScaleRegime {
    if (!reach || size === undefined) return "scale";
    if (reach.via !== "morph") return "scale";
    if (size > reach.rest && !reach.canGrow) return "scale";
    if (size < reach.rest && !reach.canShrink) return "scale";
    const EPS = 1e-6;
    // A kit frame held at its floor is not being shaped below it.
    if (reach.floor !== undefined && size < reach.floor - EPS) return "scale";
    return size > reach.max + EPS || size < reach.min - EPS ? "scale" : "morph";
}

/**
 * The regime for the drag as a whole, which is what one box colour has to say.
 *
 * `morph` only while EVERY axis being asked for is still inside its range: the
 * moment one axis starts stretching, the component is no longer purely being
 * shaped, and claiming otherwise would be the ambiguity this exists to remove.
 *
 * An axis resting AT its rest size does not vote — not moving is not
 * stretching — so a fresh selection reads as `morph` and the box is purple
 * before any drag. That is deliberate: the colour states what the next drag
 * will do, not only what the last one did.
 */
export function dragRegime(
    capability: StretchCapability,
    size: Partial<Record<"width" | "height" | "depth", number>>,
): SmartScaleRegime {
    for (const { axis, field } of SMART_AXES) {
        const requested = size[field];
        if (requested === undefined) continue;
        const reach = capability[axis];
        if (reach && Math.abs(requested - reach.rest) < 1e-6) continue;
        if (axisRegime(reach, requested) === "scale") return "scale";
    }
    return "morph";
}

/** The MorphStretch behavior on this object, if it carries one. */
export function morphStretchOf(object: SceneObject | null | undefined): MorphStretch | null {
    return object?.getBehavior(MorphStretch) ?? null;
}

/**
 * The LayoutItem that sizes `object`, or null when no Layout does.
 *
 * A node is sized by layout when it has a LayoutItem and a Layout stands above
 * it; the solve then feeds MorphStretch the LayoutItem's box. Without the
 * LayoutItem layout positions the node but leaves its size alone, and without a
 * Layout above, nothing solves — in both MorphStretch's own size is the box.
 */
export function layoutItemSizing(object: SceneObject | null | undefined): LayoutItem | null {
    const item = object?.getBehavior(LayoutItem) ?? null;
    const parent = object?.getParent();
    return item && parent && LayoutItem.nearestLayoutAuthority(parent) ? item : null;
}

/**
 * The size a piece shows on `field` before a drag: the box a Layout resolved
 * for it, else MorphStretch's authored size, else rest.
 *
 * The resolved box is what is drawn inside a layout, on a Fill or Hug axis as
 * much as a Fixed one, and the authored data never holds it. A non-positive
 * resolved axis (Fill of nothing) shows the rest shape, as `MorphStretch` does.
 */
export function shownSize(
    stretch: MorphStretch,
    field: "width" | "height" | "depth",
    rest: number | undefined,
): number | undefined {
    const positive = (v: unknown): v is number => typeof v === "number" && v > 0;
    const box = stretch.getResolvedSize();
    if (box) {
        const resolved = box[SMART_AXES.find((a) => a.field === field)!.key];
        return positive(resolved) ? resolved : rest;
    }
    const authored = stretch.getData()[field];
    return positive(authored) ? authored : rest;
}

/** A box gizmo this drive can paint. Duck-typed: Babylon's `BoundingBoxGizmo` fits. */
export interface SmartScaleBox {
    setColor?: (color: Color3) => void;
    /**
     * How much of a pointer drag becomes scale. Babylon defaults this to `0.2`,
     * so the box moves five times further than the geometry — which reads as
     * "I dragged to the edge and it barely grew".
     *
     * A size drag has to track the handle, because the handle is where the user
     * is saying the component should END. {@link createSmartScaleRig} sets it
     * to 1 while a smart component is attached and puts the host's own value
     * back afterwards, so ordinary scaling keeps whatever feel the host chose.
     */
    scaleDragSpeed?: number;
}

export interface SmartScaleDrive {
    /** True while a drag is being routed into MorphStretch rather than scaling. */
    readonly active: boolean;
    /** The regime last painted — what the box is currently saying. */
    readonly regime: SmartScaleRegime | null;
    /** Paint the box for a selection that is not being dragged yet. */
    paintForSelection(object: SceneObject | null | undefined): void;
    /** Begin a gesture. Does nothing for an object with no morph reach. */
    begin(object: SceneObject | null | undefined): void;
    /** One drag tick, given the size the gizmo is showing in the object's own
     *  frame. Returns true when the drive consumed it. */
    update(liveLocalSize: Vec3Like | null | undefined): boolean;
    /** End the gesture and return its undo entry, or null when nothing changed.
     *  A size drag usually also MOVES the object; that transform belongs in the
     *  same entry, so the caller composes both. */
    end(): UndoCommand | null;
}

/**
 * @param getBox - the gizmo to paint, read lazily: hosts recreate theirs when
 *   the selection changes, so holding a reference would paint a dead one.
 */
/** Distinguishes gestures across every drive — two rigs on one object must not share keys either. */
let GESTURE_SEQ = 0;

export function createSmartScaleDrive(getBox: () => SmartScaleBox | null): SmartScaleDrive {
    let behavior: MorphStretch | null = null;
    let gestureKey = "morph:box";
    let object: SceneObject | null = null;
    let capability: StretchCapability | null = null;
    let before: MorphStretchData | null = null;
    /** Set when a Layout sizes the node: the LayoutItem the drag writes, and its data from before the drag. */
    let item: LayoutItem | null = null;
    let itemBefore: LayoutItemData | null = null;
    /** The box each axis was drawn at when the drag began, in the frame the drag reports sizes in. */
    let startSize: Partial<Record<"width" | "height" | "depth", number>> = {};
    /** Axes the drag has moved. On a node a Layout sizes only these are written, and they stay written. */
    const moved = new Set<"width" | "height" | "depth">();
    let lastRegime: SmartScaleRegime | null = null;

    const paint = (regime: SmartScaleRegime): void => {
        if (regime === lastRegime) return;
        lastRegime = regime;
        try {
            getBox()?.setColor?.(Color3.FromHexString(colorForRegime(regime)));
        } catch (err) {
            Logger.warn(`[smartScale] gizmo recolor failed: ${String(err)}`);
        }
    };

    // On a node a Layout sizes, only the axes the drag has moved are written,
    // to the LayoutItem and to MorphStretch alike. An axis it has not touched
    // keeps its mode, so dragging a width does not turn a Fill height into a
    // literal, and keeps its data, so no size is stored that the next drag
    // would take for its baseline.
    const movedOnly = (size: Partial<MorphStretchData>): Partial<MorphStretchData> => {
        const out: Partial<MorphStretchData> = {};
        for (const { field } of SMART_AXES) {
            const value = size[field];
            if (value === undefined) continue;
            if (Math.abs(value - (startSize[field] ?? 0)) > SIZE_EPSILON) moved.add(field);
            if (moved.has(field)) out[field] = value;
        }
        return out;
    };

    // The LayoutItem half of a drag on a node a Layout sizes: a Fixed size on
    // each moved axis.
    const writeItem = (size: Partial<MorphStretchData>): void => {
        if (!item) return;
        const patch: Partial<LayoutItemData> = {};
        for (const { field, mode } of SMART_AXES) {
            const value = size[field];
            if (value === undefined) continue;
            patch[field] = value;
            patch[mode] = "Fixed";
        }
        void item.commit(patch).then(
            (errors) => {
                if (errors.length > 0) Logger.warn(`[smartScale] live layout size refused: ${errors.map((e) => e.message).join('; ')}`);
            },
            (err: unknown) => Logger.warn(`[smartScale] live layout size failed: ${String(err)}`),
        );
        try {
            const path = object?.getPath?.();
            if (path) dispatchBehaviorChanged({ objectPath: path, behaviorType: "LayoutItem", action: "updated" });
        } catch (err) {
            Logger.warn(`[smartScale] behavior-changed dispatch failed: ${String(err)}`);
        }
    };

    // `commit` returns a promise, but the scene mutation inside it is
    // SYNCHRONOUS — only the promise is deferred. So every tick is written
    // straight through, in call order, and the promise settles on its own. An
    // earlier version awaited each write and coalesced the ticks that arrived
    // meanwhile; that dropped intermediate sizes, so the geometry lagged the
    // handle and then jumped to catch up.
    const write = (size: Partial<MorphStretchData>): void => {
        if (!behavior) return;
        const target = object;
        const patch = item ? movedOnly(size) : size;
        // A first tick that has not moved anything yet writes nothing.
        if (Object.keys(patch).length === 0) return;
        writeItem(patch);
        // `commit` is async, so a failure is always a rejection; a refused
        // patch is a RESOLVED error list. Both arms, or a failed tick is silent.
        void behavior.commit(patch).then(
            (errors) => {
                if (errors.length > 0) Logger.warn(`[smartScale] live size write refused: ${errors.map((e) => e.message).join('; ')}`);
            },
            (err: unknown) => Logger.warn(`[smartScale] live size write failed: ${String(err)}`),
        );
        // A size panel reads on editor:behavior-changed, and `commit` does not
        // dispatch it — without this the panel sits at its old numbers while
        // the geometry moves under the drag.
        try {
            const path = target?.getPath?.();
            if (path) dispatchBehaviorChanged({ objectPath: path, behaviorType: "MorphStretch", action: "updated" });
        } catch (err) {
            Logger.warn(`[smartScale] behavior-changed dispatch failed: ${String(err)}`);
        }
    };

    return {
        get active() { return !!behavior && !!capability; },
        get regime() { return lastRegime; },

        paintForSelection(obj) {
            // A plain asset keeps the ordinary blue box; a smart one goes
            // purple on selection, before any drag.
            paint(morphStretchOf(obj)?.getStretchCapability() ? "morph" : "scale");
        },

        begin(obj) {
            const b = morphStretchOf(obj);
            const reach = b?.getStretchCapability() ?? null;
            if (!b || !reach) {
                behavior = null;
                capability = null;
                object = null;
                return;
            }
            behavior = b;
            gestureKey = `morph:box#${++GESTURE_SEQ}`;
            capability = reach;
            object = obj ?? null;
            before = { ...b.getData() };
            item = layoutItemSizing(obj);
            itemBefore = item ? { ...item.getData() } : null;
            moved.clear();
            // The drag's baseline is the box the rig drew (see `shownSize`):
            // measured from anything else, a Fill axis jumps on its first tick.
            startSize = {};
            if (item) {
                for (const { axis, field } of SMART_AXES) {
                    startSize[field] = shownSize(b, field, reach[axis]?.rest);
                }
            }
            paint("morph");
        },

        update(liveLocalSize) {
            if (!behavior || !capability || !liveLocalSize) return false;
            const want = requestedSize(capability, liveLocalSize);
            if (Object.keys(want).length === 0) return false;

            // The FULL requested size goes to MorphStretch and the engine does
            // the overflow itself. An earlier version split it here and wrote
            // the remainder to `transform.scaling`; it compounded, because the
            // base was read from a transform that already held the previous
            // overflow — a second drag to the same size doubled it, and
            // shrinking back into range stranded the old scale.
            paint(dragRegime(capability, want));
            write(want);
            return true;
        },

        end() {
            const obj = object;
            const b = behavior;
            const beforeData = before;
            const sizing = item;
            const sizingBefore = itemBefore;
            behavior = null;
            capability = null;
            object = null;
            before = null;
            item = null;
            itemBefore = null;
            // Selection survives the drag, so leave the box purple for the next.
            paint("morph");
            if (!obj || !b || !beforeData) return null;
            try {
                const after = { ...b.getData() };
                // Keyed to THIS gesture. The undo stack merges same-key commits that
                // land within its window, which is right for the ticks of one
                // drag and wrong for two drags in quick succession — the second
                // would fold into the first and its starting size would be lost.
                const commands: UndoCommand[] = [];
                if (sizing && sizingBefore) {
                    const sizingAfter = { ...sizing.getData() };
                    if (JSON.stringify(sizingBefore) !== JSON.stringify(sizingAfter)) {
                        commands.push(new BehaviorCommitCommand(obj, "LayoutItem", sizingBefore, sizingAfter, gestureKey));
                    }
                }
                if (SMART_AXES.some(({ field }) => beforeData[field] !== after[field])) {
                    commands.push(new BehaviorCommitCommand(obj, "MorphStretch", beforeData, after, gestureKey));
                }
                if (commands.length <= 1) return commands[0] ?? null;
                // Both halves are one size: undo must not let the layout solve between them.
                return new CompositeCommand(`Resize ${obj.name}`, commands, { settle: [{ object: obj, path: obj.getPath() }] });
            } catch (err) {
                Logger.warn(`[smartScale] command build failed: ${String(err)}`);
                return null;
            }
        },
    };
}
